// Board (arena) skins: shop items (kind "board") that restyle YOUR view of the
// arena - a drawn floor and wall (see board-skin-draw.ts). Unlike heads and
// victory animations, the board is a VIEWER preference (everyone shares one
// arena, so your skin is how *you* see every match), which is why it lives in
// localStorage and is never sent to the server - the same model MS Battle uses.
// Ownership is enforced at equip time in the shop.

import { gateCosmetic } from "../shared/cosmetics-gate";

export interface BoardSkin {
  id: string;
  label: string;
  // Fallback flat colours (used if the drawn skin is unavailable) and the
  // colours the drawn floor/wall are built around.
  bg: string;
  border: string;
}

// The launch catalog, in shop order. Each id has a drawn floor + wall pass in
// board-skin-draw.ts (designed on docs/design/arena-skins/arena-stage.html).
export const BOARD_SKINS: BoardSkin[] = [
  { id: "midnight", label: "Midnight Blue", bg: "#0a1230", border: "#3a558a" },
  { id: "ember", label: "Ember", bg: "#160c0a", border: "#8a2e1c" },
  { id: "mono", label: "Monochrome", bg: "#000000", border: "#e8e8e8" },
  { id: "retro", label: "Retro Green", bg: "#05200c", border: "#0a2e12" },
  { id: "checker", label: "Chess", bg: "#1a1a1f", border: "#f4f4f6" },
  { id: "hive", label: "Bee Hive", bg: "#1a1206", border: "#7a4f22" },
  { id: "nebula", label: "Nebula", bg: "#05030f", border: "#2a2d36" },
  { id: "dungeon", label: "Dungeon", bg: "#151310", border: "#2e2b27" },
  { id: "gold", label: "I am rich", bg: "#121008", border: "#8a6212" },
];

export function boardSkinById(id: string | null | undefined): BoardSkin | null {
  if (!id) return null;
  return BOARD_SKINS.find((s) => s.id === id) || null;
}

const KEY = "ar.boardSkin";
// Temporary override while the shop's "try it" match runs: wins over the
// equipped skin, never persisted. Cleared when the try-out page unmounts.
let previewSkin: string | null = null;
export function setBoardSkinPreview(id: string | null): void {
  previewSkin = id;
}
// The arena the match itself calls for, for everybody in it and whatever they
// have equipped: the Royale's final is played in the gold one. Set by the game
// when a round is announced, cleared when it is another kind of round or the
// game is left.
let matchSkin: string | null = null;
export function setMatchBoardSkin(id: string | null): void {
  matchSkin = id;
}
export function getEquippedBoardSkin(): string | null {
  if (matchSkin) return matchSkin;
  if (previewSkin) return previewSkin; // try-outs preview regardless of sign-in
  try {
    // Paid skins render only for a signed-in player (the stored equip stays).
    return gateCosmetic("board", localStorage.getItem(KEY) || null);
  } catch {
    return null;
  }
}
export function setEquippedBoardSkin(id: string | null): void {
  try {
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
